import {
  ProjectDto,
  SpoolUpdateForm,
  useBulkUpdateSpoolsSilentMutation,
  invalidateBulkNodeKeys,
  refreshMineModel
} from 'providers/api';
import {
  useState,
  useRef,
  useEffect,
  useCallback
} from 'react';
import { useQueryClient } from 'react-query';
import { invalidate } from '@react-three/fiber';
import { Model } from '../../hooks/useModelState';
import PCPipe from '../../MineModel/Pipes/PCPipe';
import PCSpool from '../../MineModel/Spools/PCSpool';
import { PIPE_COLOURS } from '../../MineModel/GConstants';

interface UseChangePipesProps {
  model: Model;
  project: ProjectDto;
  selectedPipeType: string | null;
}

interface PendingSpool {
  spool: PCSpool;
  originalPipe: PCPipe;
  originalColour: string;
}

const DEBOUNCE_MS = 300;

const resolvePipeColour = (pipe: PCPipe): string => {
  const parsed = parseInt(pipe.pipeId, 10);
  const index = Number.isFinite(parsed) ? Math.max(0, parsed - 1) : 0;
  return pipe.colour || PIPE_COLOURS[index] || PIPE_COLOURS[0];
};

const useChangePipes = ({ model, project, selectedPipeType }: UseChangePipesProps) => {
  const [isSelectingSpools, setIsSelectingSpools] = useState(false);
  const [selectedSpool, setSelectedSpool] = useState<PCSpool | null>(null);
  const selectedSpoolRef = useRef<PCSpool | null>(null);
  const previousSpoolColourRef = useRef<string | null>(null);
  const selectedPipeTypeRef = useRef<string | null>(selectedPipeType);
  const bulkUpdateSpoolsMutation = useBulkUpdateSpoolsSilentMutation(project.entityId);
  const queryClient = useQueryClient();
  const pendingSpoolsRef = useRef<Map<string, PendingSpool>>(new Map());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushInFlightRef = useRef<boolean>(false);

  // The pipe is a property of the spool, so every pending change is one entry in one patch. The whole
  // batch goes in a single request: the reticulation document is one per mine and parallel saves race.
  const flushPendingSpools = useCallback(() => {
    const pending = pendingSpoolsRef.current;
    if (pending.size === 0) return;
    if (flushInFlightRef.current) {
      flushTimerRef.current = setTimeout(flushPendingSpools, DEBOUNCE_MS);
      return;
    }

    const entries = Array.from(pending.values());
    pendingSpoolsRef.current = new Map();
    flushInFlightRef.current = true;

    // setSpoolProperties mutates the spool in place, so the captured reference already carries the new pipe.
    const updates: SpoolUpdateForm[] = entries.map(({ spool }) => ({ spoolId: spool.spoolId, pipeId: spool.pipe.pipeId }));

    bulkUpdateSpoolsMutation.mutateAsync(
      { projectId: project.entityId, spools: updates },
    ).catch(() => {
      entries.forEach(({ spool, originalPipe, originalColour }) => {
        model.setSpoolProperties(spool.spoolId, { pipe: originalPipe, spoolColour: originalColour });
      });
      invalidateBulkNodeKeys(queryClient);
    }).finally(() => {
      flushInFlightRef.current = false;
      if (pendingSpoolsRef.current.size > 0) {
        flushTimerRef.current = setTimeout(flushPendingSpools, DEBOUNCE_MS);
        return;
      }
      // Refreshing per flush would rebuild the scene mid-paint, so the cached model catches up once the queue is empty.
      refreshMineModel(queryClient);
    });
  }, [model, bulkUpdateSpoolsMutation, project, queryClient]);

  const handleSpoolSelect = (spool: PCSpool | undefined) => {
    const currentPipeType = selectedPipeTypeRef.current;
    if (!isSelectingSpools || !currentPipeType || !model || !spool) return;

    const newPipe = Object.values(model.pipes).find((pipe) => pipe.name === currentPipeType);
    if (!newPipe) return;

    const originalPipe = spool.pipe;
    const originalColour = spool.spoolColour;

    model.setSpoolProperties(spool.spoolId, { pipe: newPipe, spoolColour: resolvePipeColour(newPipe) });

    pendingSpoolsRef.current.set(spool.spoolId, { spool, originalPipe, originalColour });

    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
    }
    flushTimerRef.current = setTimeout(flushPendingSpools, DEBOUNCE_MS);
  };

  // Marquee path: every selected spool is patched in one request.
  const handleBulkSpoolsChange = useCallback(async (spools: PCSpool[]) => {
    const currentPipeType = selectedPipeTypeRef.current;
    if (!currentPipeType || !model || spools.length === 0) return;

    const newPipe = Object.values(model.pipes).find((pipe) => pipe.name === currentPipeType);
    if (!newPipe) return;

    const pipeColour = resolvePipeColour(newPipe);
    const originalStates = spools.map((spool) => ({
      spoolId: spool.spoolId,
      pipe: spool.pipe,
      spoolColour: spool.spoolColour,
    }));

    spools.forEach((spool) => model.setSpoolProperties(spool.spoolId, { pipe: newPipe, spoolColour: pipeColour }));

    invalidate();

    try {
      const updates: SpoolUpdateForm[] = spools.map((spool) => ({ spoolId: spool.spoolId, pipeId: newPipe.pipeId }));
      await bulkUpdateSpoolsMutation.mutateAsync({ projectId: project.entityId, spools: updates });
      refreshMineModel(queryClient);
    } catch (e) {
      originalStates.forEach((original) => {
        model.setSpoolProperties(original.spoolId, { pipe: original.pipe, spoolColour: original.spoolColour });
      });
      invalidateBulkNodeKeys(queryClient);
      invalidate();
    }
  }, [model, bulkUpdateSpoolsMutation, project, queryClient]);

  const handleSelectForChangePipe = () => {
    if (!selectedPipeType) return;
    setIsSelectingSpools(true);
    setSelectedSpool(null);
    selectedSpoolRef.current = null;
    previousSpoolColourRef.current = null;
  };

  const handleCancelSelection = () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    pendingSpoolsRef.current.clear();

    if (selectedSpoolRef.current && model) {
      if (previousSpoolColourRef.current) {
        model.setSpoolColour(selectedSpoolRef.current.spoolId, previousSpoolColourRef.current);
      }
      invalidate();
    }

    setIsSelectingSpools(false);
    setSelectedSpool(null);
    selectedSpoolRef.current = null;
    previousSpoolColourRef.current = null;
  };

  const invalidateModel = useCallback(() => {
    invalidateBulkNodeKeys(queryClient);
  }, [queryClient]);

  useEffect(() => {
    selectedPipeTypeRef.current = selectedPipeType;
  }, [selectedPipeType]);

  useEffect(() => () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
    }
  }, []);

  return {
    isSelectingNodes: isSelectingSpools,
    selectedNode: selectedSpool,
    handleSelectForChangePipe,
    handleCancelSelection,
    handleSpoolSelect,
    handleBulkSpoolsChange,
    invalidateModel,
  };
};

export default useChangePipes;
